import { gsap } from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import Alpine from 'alpinejs';
gsap.registerPlugin(ScrollTrigger);

export default () => ({
    originalHtml: null,
    scrollTrigger: null,
    resizeObserver: null,
    resizeTimeout: null,
    duration: 1.4,
    delay: 0,
    stagger: 0.1,
    ease: "expo.out",

    init() {
        this.originalHtml = this.$el.innerHTML;

        if (this.$el.dataset.delay !== undefined) {
            this.delay = parseFloat(this.$el.dataset.delay);
        }

        this.$nextTick(() => {
            this.setup();
        });

        this.resizeObserver = new ResizeObserver(() => {
            clearTimeout(this.resizeTimeout);
            this.resizeTimeout = setTimeout(() => {
                this.setup();
            }, 150);
        });
        this.resizeObserver.observe(this.$el);
    },
    destroy() {
        this.kill();

        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }

        clearTimeout(this.resizeTimeout);
    },
    setup() {
        this.kill();
        this.restore();
        this.wrapCharacters();
        this.wrapLines();
        this.initNestedComponents();
        this.animate();
    },
    kill() {
        if (this.scrollTrigger) {
            this.scrollTrigger.kill();
            this.scrollTrigger = null;
        }
    },
    restore() {
        if (this.originalHtml !== null) {
            this.$el.innerHTML = this.originalHtml;
        }
    },
    wrapCharacters() {
        const walker = document.createTreeWalker(
            this.$el,
            NodeFilter.SHOW_TEXT,
            {
                acceptNode: node => {
                    if (!node.nodeValue || !/[^\t\n\r\f ]/.test(node.nodeValue)) {
                        return NodeFilter.FILTER_REJECT;
                    }

                    if (node.parentElement.closest('script, style, template, .tooltip, .scroll-reveal__line, .scroll-reveal__word, .scroll-reveal__char')) {
                        return NodeFilter.FILTER_REJECT;
                    }

                    return NodeFilter.FILTER_ACCEPT;
                }
            }
        );
        const textNodes = [];

        while (walker.nextNode()) {
            textNodes.push(walker.currentNode);
        }

        textNodes.forEach(textNode => {
            const fragment = document.createDocumentFragment();
            const parts = textNode.nodeValue.match(/[^\s\u00a0]+(?:\u00a0[^\s\u00a0]+)*|\s+/g) || [];

            parts.forEach(part => {
                if (/^\s+$/.test(part)) {
                    fragment.appendChild(document.createTextNode(part));
                    return;
                }

                const word = document.createElement('span');
                word.className = 'scroll-reveal__word';

                Array.from(part).forEach(char => {
                    const span = document.createElement('span');
                    span.className = 'scroll-reveal__char';
                    span.textContent = char;
                    word.appendChild(span);
                });

                fragment.appendChild(word);
            });

            textNode.replaceWith(fragment);
        });

        this.$el.querySelectorAll('img').forEach((image, index) => {
            image.classList.add('scroll-reveal__image');
            image.classList.add(`scroll-reveal__image--${index + 1}`);
        });
    },
    wrapLines() {
        const tokens = gsap.utils.toArray(this.$el.querySelectorAll('.scroll-reveal__word, .scroll-reveal__image, .tooltip, br'));
        const lines = [];
        const breaks = [];
        let currentLine = [];
        let currentLineRect = null;

        const pushLine = () => {
            if (currentLine.length) {
                lines.push(currentLine);
            }

            currentLine = [];
            currentLineRect = null;
        };

        const rectsOverlapY = (rect, lineRect) => {
            return rect.top < lineRect.bottom && rect.bottom > lineRect.top;
        };

        const mergeLineRect = rect => {
            if (!currentLineRect) {
                currentLineRect = {
                    top: rect.top,
                    bottom: rect.bottom,
                };

                return;
            }

            currentLineRect.top = Math.min(currentLineRect.top, rect.top);
            currentLineRect.bottom = Math.max(currentLineRect.bottom, rect.bottom);
        };

        tokens.forEach(token => {
            if (token.tagName === 'BR') {
                pushLine();
                breaks.push(token);
                return;
            }

            const rect = token.getBoundingClientRect();

            if (currentLineRect && !rectsOverlapY(rect, currentLineRect)) {
                pushLine();
            }

            currentLine.push(token);
            mergeLineRect(rect);
        });

        pushLine();
        breaks.forEach(br => br.remove());

        lines.forEach(line => {
            const wrapper = document.createElement('span');
            wrapper.className = 'scroll-reveal__line';
            line[0].before(wrapper);

            line.forEach(token => {
                let sibling = wrapper.nextSibling;

                while (sibling && sibling !== token) {
                    const nextSibling = sibling.nextSibling;
                    wrapper.appendChild(sibling);
                    sibling = nextSibling;
                }

                wrapper.appendChild(token);
            });
        });
    },
    animate() {
        const targets = gsap.utils.toArray(this.$el.querySelectorAll('.scroll-reveal__char, .scroll-reveal__image'));

        if (!targets.length) {
            return;
        }

        this.scrollTrigger = ScrollTrigger.create({
            trigger: '.intro__sticker',
            start: "top top",
            end: "bottom bottom",
            scrub: true,
            once: false,
            animation: gsap.fromTo(targets, {
                //yPercent: 110,
                color: '#FFFFFF',
                opacity: 0.5,
            }, {
                //yPercent: 0,
                opacity: 1,
                color: '#42C6AC',
                duration: this.duration,
                delay: this.delay,
                stagger: this.stagger,
                ease: this.ease,
            }),
        });
    },
    initNestedComponents() {
        this.$el.querySelectorAll('[x-data]').forEach(element => {
            if (element._x_dataStack) {
                return;
            }

            Alpine.initTree(element);
        });
    },
});
